/**
 * RegulatoryNotice.tsx
 * Contextual regulatory compliance notice banner.
 * Shows applicable law, schedule, and standard reference for the active regulatory standard.
 */
import { ShieldCheck, BookOpen } from 'lucide-react';

interface RegulatoryNoticeProps {
  standard?: string | null;
  context?: 'influent' | 'effluent' | 'daf' | 'general';
  compact?: boolean;
}

const STANDARD_INFO: Record<string, {
  law: string;
  schedule: string;
  body: string;
  note: string;
}> = {
  UK_EA: {
    law: 'Environment Act 2021 & Water Industry Act 1991',
    schedule: 'Urban Wastewater Treatment Regulations 1994 (SI 1994/2841) — Schedule 2 & 3',
    body: 'Environment Agency (EA) / Ofwat',
    note: 'Discharge consents require compliance with EA permit conditions. Trade effluent consents specify limits for BOD, COD, TSS, pH, NH₃-N and trade-specific parameters per Schedule 3 of the Trade Effluent Regulations.',
  },
  AU_EPA: {
    law: 'Environment Protection Act 1970 (Vic) / Protection of the Environment Operations Act 1997 (NSW)',
    schedule: 'ANZECC/ARMCANZ 2000 Water Quality Guidelines — Schedule A (Discharge Limits)',
    body: 'State EPA (VIC / NSW / QLD / WA)',
    note: 'Environment Protection Licence (EPL) conditions specify discharge limits under State EPA regulations. Applies to BOD₅, TSS, pH, ammonia-N, total phosphorus, and oil & grease per ANZECC 2000 guidelines.',
  },
  EU_UWWTD: {
    law: 'EU Urban Wastewater Treatment Directive 91/271/EEC (as amended by Directive 98/15/EC)',
    schedule: 'Annex I — Requirements for Discharges from Urban Wastewater Treatment Plants',
    body: 'National Environmental Authority (EU Member State)',
    note: 'Annex I Table 1 & 2 specify concentration limits and removal efficiency percentages for BOD₅, COD, TSS, total nitrogen, and total phosphorus. Sensitive area discharges require enhanced tertiary treatment (Annex II).',
  },
};

const CONTEXT_NOTE: Record<string, Record<string, string>> = {
  influent: {
    UK_EA: 'Influent characterisation data is required for trade effluent consent applications and EA compliance monitoring returns.',
    AU_EPA: 'Influent monitoring forms part of the annual environmental monitoring program required under EPL conditions.',
    EU_UWWTD: 'Influent data must be retained for minimum 2 years as required under Directive 91/271/EEC Article 16.',
  },
  effluent: {
    UK_EA: 'Final effluent must comply with EA discharge consent limits at the point of discharge to controlled waters (WFD Article 4).',
    AU_EPA: 'Treated effluent discharge must comply with EPL numeric limits at all times — exceedances must be reported to State EPA within 24 hours.',
    EU_UWWTD: 'Treated wastewater must meet Annex I Directive 91/271/EEC limits. Failure to comply may trigger formal infringement proceedings.',
  },
  daf: {
    UK_EA: 'DAF outlet quality must be verified against EA consent limits for the receiving watercourse. Sludge disposal requires separate EA approval.',
    AU_EPA: 'DAF outlet parameters must meet EPL effluent limits. Sludge produced is a controlled waste under State EPA regulations.',
    EU_UWWTD: 'Pre-treatment (including DAF) must achieve sufficient reduction to allow downstream biological treatment to meet Annex I limits.',
  },
  general: {
    UK_EA: 'All wastewater data must be retained per EA record-keeping requirements (minimum 4 years per Water Resources Act 1991).',
    AU_EPA: 'All monitoring data must be retained for minimum 4 years per State EPA licence conditions.',
    EU_UWWTD: 'Records must be maintained per Directive 91/271/EEC Article 16 — 2 years minimum retention.',
  },
};

export default function RegulatoryNotice({ standard, context = 'general', compact = false }: RegulatoryNoticeProps) {
  const code = standard || 'UK_EA';
  const info = STANDARD_INFO[code] || STANDARD_INFO.UK_EA;
  const ctxNote = CONTEXT_NOTE[context]?.[code] || CONTEXT_NOTE.general[code] || '';

  if (compact) {
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg border border-amber-500/20 bg-amber-500/5">
        <ShieldCheck className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-xs font-bold text-amber-400">{info.law}</p>
          <p className="text-xs text-slate-400">{info.schedule}</p>
          {ctxNote && <p className="text-xs text-slate-500 mt-0.5">{ctxNote}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-amber-500/10 bg-amber-500/8">
        <ShieldCheck className="w-4 h-4 text-amber-400 shrink-0" />
        <p className="text-xs font-black text-amber-400 uppercase tracking-wider">Regulatory Compliance Requirement</p>
        <span className="ml-auto px-2 py-0.5 rounded text-xs font-bold bg-amber-500/15 text-amber-300 border border-amber-500/25">
          {code.replace('_', ' ')}
        </span>
      </div>
      <div className="px-4 py-3 space-y-1.5">
        <div className="flex items-start gap-2">
          <BookOpen className="w-3.5 h-3.5 text-amber-500/70 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-slate-300">{info.law}</p>
            <p className="text-xs text-slate-400">{info.schedule}</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-amber-500/70 shrink-0 mt-0.5" />
          <p className="text-xs text-slate-400"><span className="font-bold text-slate-300">Regulatory body: </span>{info.body}</p>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed pt-0.5">{info.note}</p>
        {ctxNote && (
          <p className="text-xs text-amber-400/80 italic border-t border-amber-500/10 pt-1.5">{ctxNote}</p>
        )}
      </div>
    </div>
  );
}
